import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  useNavigate,
  Link,
  useLocation,
  useBeforeUnload,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Sparkles,
  Loader2,
  Cpu,
  FileText,
  ChevronDown,
} from "lucide-react";
import { sopsAPI, machinesAPI, knowledgeAPI } from "../api";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import clsx from "clsx";

// A generic custom dropdown component
const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  loading,
  disabled,
  renderOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options?.find((opt) => (opt.id || opt._id) === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          "input input-field text-left w-full flex justify-between items-center",
          disabled && "bg-slate-100 cursor-not-allowed",
        )}
      >
        <span className={clsx(!selectedOption && "text-slate-400")}>
          {selectedOption ? renderOption(selectedOption) : placeholder}
        </span>
        <ChevronDown
          className={clsx(
            "w-4 h-4 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-10 mt-1 w-full card bg-white p-1 shadow-lg max-h-60 overflow-y-auto"
          >
            {loading && (
              <div className="px-3 py-2 text-sm text-slate-500">Loading...</div>
            )}
            {!loading &&
              options?.map((option) => (
                <button
                  type="button"
                  key={option.id || option._id}
                  onClick={() => {
                    onChange(option.id || option._id);
                    setIsOpen(false);
                  }}
                  className="dropdown-item w-full text-left"
                >
                  {renderOption(option)}
                </button>
              ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CreateSOP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [isAiMode, setIsAiMode] = useState(false);
  const [isReviewingDraft, setIsReviewingDraft] = useState(false); //
  const [aiInputs, setAiInputs] = useState({ title: "", purpose: "" });
  const [selectedDoc, setSelectedDoc] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");

  const [formData, setFormData] = useState({
    machineId: "",
    title: "",
    description: "",
    category: "Operation",
    estimatedDuration: 30,
    skillLevel: "Intermediate",
    steps: [
      {
        order: 1,
        instruction: "",
        warning: "",
        checkPoint: false,
        requiresImage: false,
      },
    ],
    safetyWarnings: [""],
    requiredTools: [""],
    tags: "",
  });

  const generateMutation = useMutation({
    mutationFn: (data) => sopsAPI.generate(data),
    onSuccess: (res) => {
      const generated = res.data.data;
      // Fixed: Defensive checks for arrays to prevent .map() failures
      setFormData((prev) => ({
        ...prev,
        title: generated.title || aiInputs.title,
        description: generated.description || aiInputs.purpose,
        steps: Array.isArray(generated.steps) ? generated.steps : prev.steps,
        safetyWarnings: Array.isArray(generated.safety_warnings)
          ? generated.safety_warnings
          : prev.safetyWarnings,
        requiredTools: Array.isArray(generated.required_tools)
          ? generated.required_tools
          : prev.requiredTools,
        estimatedDuration:
          generated.estimated_duration || prev.estimatedDuration,
        machineId: selectedMachine,
      }));
      setIsAiMode(false);
      setIsReviewingDraft(true); // Hide AI toggle after successful draft
      toast.success("AI Draft Generated! Review and edit details below.");
    },
    onError: (err) =>
      toast.error(
        "AI failed to generate: " + (err.response?.data?.error || err.message),
      ),
  });

  const isGenerating = generateMutation.isPending;
  useBeforeUnload(
    useCallback(
      (event) => {
        if (isGenerating) {
          event.preventDefault();
          return (event.returnValue = "");
        }
      },
      [isGenerating],
    ),
  );

  useEffect(() => {
    if (location.state?.initialData) {
      const data = location.state.initialData;
      setIsReviewingDraft(true); // Hide AI toggle if loaded from Assistant
      setFormData((prev) => ({
        ...prev,
        machineId: data.machineId || prev.machineId,
        title: data.title || prev.title,
        description: data.description || prev.description,
        estimatedDuration: data.estimated_duration || prev.estimatedDuration,
        // Fixed: Ensure arrays are valid
        requiredTools: Array.isArray(data.required_tools)
          ? data.required_tools
          : prev.requiredTools,
        safetyWarnings: Array.isArray(data.safety_warnings)
          ? data.safety_warnings
          : prev.safetyWarnings,
        steps:
          data.steps?.map((s, i) => ({
            order: i + 1,
            instruction: s.instruction || "",
            warning: s.warning || "",
            checkPoint: !!s.checkPoint,
            requiresImage: !!s.requiresImage,
          })) || prev.steps,
      }));
      toast.success("SOP drafted from AI Assistant");
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const { data: machines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => res.data.data),
  });

  // Filtered Knowledge Documents based on selected Target Machine
  const { data: documents } = useQuery({
    queryKey: ["knowledge-docs", selectedMachine],
    queryFn: () =>
      knowledgeAPI
        .getAll({
          status: "approved",
          machineId: selectedMachine, // Passing machine filter to API
        })
        .then((res) => res.data.data),
    enabled: isAiMode && !!selectedMachine,
  });

  const createMutation = useMutation({
    mutationFn: (data) => sopsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sops"] });
      toast.success("SOP created successfully");
      navigate("/sops");
    },
    onError: (err) =>
      toast.error(err.response?.data?.error || "Failed to create SOP"),
  });

  const handleGenerate = () => {
    if (
      !selectedDoc ||
      !selectedMachine ||
      !aiInputs.title ||
      !aiInputs.purpose
    ) {
      toast.error("Please fill in all fields (Title, Purpose, Doc, Machine)");
      return;
    }
    generateMutation.mutate({
      documentId: selectedDoc,
      machineId: selectedMachine,
      title: aiInputs.title,
      purpose: aiInputs.purpose,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Client-side validation: description must be at least 100 characters
    if (!formData.description || formData.description.trim().length < 100) {
      toast.error('Description is required and must be at least 100 characters');
      return;
    }
    const data = {
      ...formData,
      tags:
        typeof formData.tags === "string"
          ? formData.tags
              .split(",")
              .map((t) => t.trim())
              .filter((t) => t)
          : [],
      steps: formData.steps.map((s, i) => ({ ...s, order: i + 1 })),
    };
    createMutation.mutate(data);
  };

  // Word count helper and description change handler to enforce 500-word max
  const countWords = (text) => {
    if (!text) return 0;
    return text
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
  };

  const handleDescriptionChange = (value) => {
    // Enforce 500-word max by trimming excess words
    const words = value.trim().split(/\s+/).filter(Boolean);
    if (words.length > 500) {
      const trimmed = words.slice(0, 500).join(' ');
      setFormData({ ...formData, description: trimmed });
      toast.error('Description limited to 500 words');
    } else {
      setFormData({ ...formData, description: value });
    }
  };

  const addStep = () =>
    setFormData({
      ...formData,
      steps: [
        ...formData.steps,
        {
          order: formData.steps.length + 1,
          instruction: "",
          warning: "",
          checkPoint: false,
          requiresImage: false,
        },
      ],
    });

  const removeStep = (index) =>
    setFormData({
      ...formData,
      steps: formData.steps.filter((_, i) => i !== index),
    });

  const updateStep = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index][field] = value;
    setFormData({ ...formData, steps: newSteps });
  };

  const handleListChange = (field, index, value) => {
    const newList = [...formData[field]];
    newList[index] = value;
    setFormData({ ...formData, [field]: newList });
  };

  const addListItem = (field) =>
    setFormData({ ...formData, [field]: [...formData[field], ""] });

  const removeListItem = (field, index) =>
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index),
    });

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12 relative">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/sops" className="btn btn-ghost btn-circle">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex flex-col">
            <h1 className="text-2xl font-bold text-slate-900 leading-tight">
              {isReviewingDraft ? "Review AI Draft" : "Create New SOP"}
            </h1>
            <p className="text-slate-500 text-sm">
              Note: Each SOP is{" "}
              <span className="font-semibold text-slate-700">
                machine-specific
              </span>{" "}
              to ensure technical accuracy and safety.
            </p>
          </div>
        </div>

        {/* Hide ConvoLink AI toggle button during draft review */}
        {!isReviewingDraft && (
          <button
            onClick={() => setIsAiMode(!isAiMode)}
            className="btn btn-secondary shadow-sm gap-2"
          >
            <Sparkles className="w-4 h-4 text-purple-500" />
            {isAiMode ? "Switch to Manual" : "Generate with ConvoLink AI"}
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isAiMode ? (
          <motion.div
            key="ai-mode"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="card p-8 border-purple-200 bg-purple-500/5 space-y-6"
          >
            <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" /> ConvoLink
              AI-Assisted Generation
            </h3>
            <div className="space-y-4">
              <div>
                <label className="label">SOP Title*</label>
                <input
                  className="input input-field"
                  placeholder="e.g. Weekly Spindle Calibration"
                  value={aiInputs.title}
                  onChange={(e) =>
                    setAiInputs({ ...aiInputs, title: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="label">Purpose / Goal*</label>
                <textarea
                  className="input input-field min-h-[60px]"
                  placeholder="e.g. To ensure precision accuracy after high-speed cycles..."
                  value={aiInputs.purpose}
                  onChange={(e) =>
                    setAiInputs({ ...aiInputs, purpose: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-primary-500" /> Target Machine*
                  </label>
                  <CustomDropdown
                    options={machines}
                    value={selectedMachine}
                    onChange={(val) => {
                      setSelectedMachine(val);
                      setSelectedDoc(""); // Reset document if machine changes
                    }}
                    placeholder="Select a machine..."
                    loading={!machines}
                    renderOption={(m) => m.name}
                  />
                </div>
                <div>
                  <label className="label flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary-500" /> Source
                    Document* (Filtered by Machine)
                  </label>
                  <CustomDropdown
                    options={documents}
                    value={selectedDoc}
                    onChange={setSelectedDoc}
                    placeholder={
                      selectedMachine
                        ? "Select a document..."
                        : "Select a machine first"
                    }
                    loading={!documents && !!selectedMachine}
                    disabled={!selectedMachine}
                    renderOption={(d) => d.title}
                  />
                </div>
              </div>
            </div>
            <button
              onClick={handleGenerate}
              disabled={
                isGenerating ||
                !aiInputs.title ||
                !aiInputs.purpose ||
                !selectedDoc
              }
              className="btn btn-primary w-full shadow-md gap-2"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-4 h-4" /> Generate SOP Draft
                </>
              )}
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="manual-mode"
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <div className="card p-8 space-y-4 bg-white border border-slate-200 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">SOP Title*</label>
                  <input
                    type="text"
                    required
                    className="input input-field"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g. Daily Spindle Warmup"
                  />
                </div>
                <div>
                  <label className="label">Target Machine*</label>
                  <select
                    required
                    className="input input-field"
                    value={formData.machineId}
                    onChange={(e) =>
                      setFormData({ ...formData, machineId: e.target.value })
                    }
                  >
                    <option value="">Select a machine...</option>
                    {machines?.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="label">Category</label>
                  <select
                    className="input input-field"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    <option>Operation</option>
                    <option>Maintenance</option>
                    <option>Safety</option>
                    <option>Troubleshooting</option>
                  </select>
                </div>
                <div>
                  <label className="label">Est. Duration (min)</label>
                  <input
                    type="number"
                    className="input input-field"
                    value={formData.estimatedDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimatedDuration: parseInt(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <label className="label">Skill Level</label>
                  <select
                    className="input input-field"
                    value={formData.skillLevel}
                    onChange={(e) =>
                      setFormData({ ...formData, skillLevel: e.target.value })
                    }
                  >
                    <option>Basic</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label">Description*</label>
                <textarea
                  className="input input-field min-h-[80px]"
                  value={formData.description}
                  minLength={100}
                  required
                  onChange={(e) => handleDescriptionChange(e.target.value)}
                />
                <p className="text-sm text-slate-500 mt-1">
                  {countWords(formData.description)} / 500 words
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card p-6 bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">
                    Safety Warnings
                  </h3>
                  <button
                    type="button"
                    onClick={() => addListItem("safetyWarnings")}
                    className="btn btn-ghost btn-xs text-primary-500"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {/* Fixed: map defensive check */}
                  {(formData.safetyWarnings || []).map((sw, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        className="input input-field text-sm"
                        value={sw}
                        onChange={(e) =>
                          handleListChange("safetyWarnings", i, e.target.value)
                        }
                      />
                      <button
                        type="button"
                        onClick={() => removeListItem("safetyWarnings", i)}
                        className="btn btn-ghost btn-square btn-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card p-6 bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">
                    Required Tools
                  </h3>
                  <button
                    type="button"
                    onClick={() => addListItem("requiredTools")}
                    className="btn btn-ghost btn-xs text-primary-500"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {/* Fixed: map defensive check */}
                  {(formData.requiredTools || []).map((rt, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        className="input input-field text-sm"
                        value={rt}
                        onChange={(e) =>
                          handleListChange("requiredTools", i, e.target.value)
                        }
                      />
                      <button
                        type="button"
                        onClick={() => removeListItem("requiredTools", i)}
                        className="btn btn-ghost btn-square btn-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="card p-6 bg-white border border-slate-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">
                  Procedure Steps
                </h3>
                <button
                  type="button"
                  onClick={addStep}
                  className="btn btn-secondary btn-sm"
                >
                  <Plus className="w-3 h-3" /> Add Step
                </button>
              </div>
              {(formData.steps || []).map((step, i) => (
                <div
                  key={i}
                  className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3 group"
                >
                  <div className="flex items-center justify-between">
                    <span className="badge badge-primary">Step {i + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeStep(i)}
                      className="btn btn-ghost btn-square btn-sm opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea
                    className="input input-field"
                    placeholder="Instruction text..."
                    value={step.instruction}
                    onChange={(e) =>
                      updateStep(i, "instruction", e.target.value)
                    }
                    rows="2"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                    <input
                      className="input input-field text-sm md:col-span-1"
                      placeholder="Warning (optional)..."
                      value={step.warning}
                      onChange={(e) => updateStep(i, "warning", e.target.value)}
                    />
                    <div className="md:col-span-2 flex items-center justify-end gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={step.checkPoint}
                          onChange={(e) =>
                            updateStep(i, "checkPoint", e.target.checked)
                          }
                          className="form-checkbox"
                        />
                        <span className="text-xs text-slate-600">
                          Checkpoint
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={step.requiresImage}
                          onChange={(e) =>
                            updateStep(i, "requiresImage", e.target.checked)
                          }
                          className="form-checkbox"
                        />
                        <span className="text-xs text-slate-600">
                          Requires Image
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={() => {
                  if (isReviewingDraft) {
                    setIsReviewingDraft(false);
                    // Optionally reset form if user cancels review
                  }
                  navigate("/sops");
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || !(formData.description && formData.description.trim().length >= 100)}
                className="btn btn-primary min-w-[150px] shadow-md"
              >
                {createMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save SOP
                  </>
                )}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreateSOP;
